/* eslint-disable max-lines */
// Copyright (c) 2015-present Mattermost, Inc. All Rights Reserved.
// See LICENSE.txt for license information.

import debounce from 'lodash/debounce';

import {SearchTypes} from 'mattermost-redux/action_types';
import {
    clearSearch,
    getPinnedPosts,
    searchPostsWithParams,
    searchFilesWithParams,
} from 'mattermost-redux/actions/search';
import * as PostActions from 'mattermost-redux/actions/posts';
import {getCurrentUserId} from 'mattermost-redux/selectors/entities/users';
import {getCurrentTeamId} from 'mattermost-redux/selectors/entities/teams';
import {getConfig} from 'mattermost-redux/selectors/entities/general';
import {getCurrentChannelId, getCurrentChannelNameForSearchShortcut} from 'mattermost-redux/selectors/entities/channels';
import {getPost} from 'mattermost-redux/selectors/entities/posts';
import {getUserTimezone} from 'mattermost-redux/selectors/entities/timezone';
import {getUserCurrentTimezone} from 'mattermost-redux/utils/timezone_utils';
import {type Action, type ActionResult, type DispatchFunc, type GenericAction, type GetStateFunc} from 'mattermost-redux/types/actions';
import {type Post} from 'mattermost-redux/types/posts';

import {getSearchTerms, getRhsState, getPluggableId, getFilesSearchExtFilter, getPreviousRhsState} from 'selectors/rhs';
import {ActionTypes, RHSStates, Constants} from 'utils/constants';
import * as Utils from 'utils/utils';
import {getBrowserUtcOffsetMinutes, getTimezoneOffsetMinutes} from 'utils/datetime';
import {type RhsState} from 'types/store/rhs';
import {type GlobalState} from 'types/store';
import {getPostsByIds} from 'mattermost-redux/actions/posts';
import {type AppDispatch} from 'stores/redux_store';

function selectPostFromRightHandSideSearchWithPreviousState(post: Post, previousRhsState?: RhsState) {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const postRootId = Utils.getRootId(post);
        await dispatch(PostActions.getPostThread(postRootId));
        const state = getState() as GlobalState;

        dispatch({
            type: ActionTypes.SELECT_POST,
            postId: postRootId,
            channelId: post.channel_id,
            previousRhsState: previousRhsState || getRhsState(state),
            timestamp: Date.now(),
        });

        return {data: true};
    };
}

function selectPostCardFromRightHandSideSearchWithPreviousState(post: Post, previousRhsState?: RhsState) {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const state = getState() as GlobalState;

        dispatch({
            type: ActionTypes.SELECT_POST_CARD,
            postId: post.id,
            channelId: post.channel_id,
            previousRhsState: previousRhsState || getRhsState(state),
        });

        return {data: true};
    };
}

export function updateRhsState(rhsState: string, channelId?: string, previousRhsState?: RhsState) {
    return (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const action = {
            type: ActionTypes.UPDATE_RHS_STATE,
            state: rhsState,
        } as GenericAction;

        if ([
            RHSStates.PIN,
            RHSStates.CHANNEL_FILES,
            RHSStates.CHANNEL_INFO,
            RHSStates.CHANNEL_MEMBERS,
        ].includes(rhsState)) {
            action.channelId = channelId || getCurrentChannelId(getState());
        }
        if (previousRhsState) {
            action.previousRhsState = previousRhsState;
        }

        dispatch(action);

        return {data: true};
    };
}

export function goBack() {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const prevState = getPreviousRhsState(getState() as GlobalState);
        dispatch({
            type: ActionTypes.RHS_GO_BACK,
            state: prevState,
        });

        return {data: true};
    };
}

export function selectPostFromRightHandSideSearch(post: Post) {
    return selectPostFromRightHandSideSearchWithPreviousState(post);
}

export function selectPostCardFromRightHandSideSearch(post: Post) {
    return selectPostCardFromRightHandSideSearchWithPreviousState(post);
}

export function selectPostFromRightHandSideSearchByPostId(postId: string) {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const post = getPost(getState(), postId);
        return selectPostFromRightHandSideSearch(post!)(dispatch, getState);
    };
}

export function updateSearchTerms(terms: string) {
    return {
        type: ActionTypes.UPDATE_RHS_SEARCH_TERMS,
        terms,
    };
}

export function updateSearchTermsForShortcut() {
    return (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const currentChannelName = getCurrentChannelNameForSearchShortcut(getState());
        return dispatch(updateSearchTerms(`in:${currentChannelName} `));
    };
}

export function updateSearchType(searchType: string) {
    return {
        type: ActionTypes.UPDATE_RHS_SEARCH_TYPE,
        searchType,
    };
}

function updateSearchResultsTerms(terms: string) {
    return {
        type: ActionTypes.UPDATE_RHS_SEARCH_RESULTS_TERMS,
        terms,
    };
}

export function performSearch(terms: string) {
    return (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const teamId = getCurrentTeamId(getState());
        const config = getConfig(getState());
        const viewArchivedChannels = config.ExperimentalViewArchivedChannels === 'true';
        const extensionsFilters = getFilesSearchExtFilter(getState() as GlobalState);

        const extensions = extensionsFilters?.map((ext) => `ext:${ext}`).join(' ');
        let termsWithExtensionsFilters = terms;
        if (extensions?.trim().length > 0) {
            termsWithExtensionsFilters += ` ${extensions}`;
        }

        // timezone offset in seconds
        const userId = getCurrentUserId(getState());
        const userTimezone = getUserTimezone(getState(), userId);
        const userCurrentTimezone = getUserCurrentTimezone(userTimezone);
        const timezoneOffset = ((userCurrentTimezone && (userCurrentTimezone.length > 0)) ? getTimezoneOffsetMinutes(userCurrentTimezone) : getBrowserUtcOffsetMinutes()) * 60;
        const messagesPromise = dispatch(searchPostsWithParams(teamId, {terms, is_or_search: false, include_deleted_channels: viewArchivedChannels, time_zone_offset: timezoneOffset, page: 0, per_page: 20}));
        const filesPromise = dispatch(searchFilesWithParams(teamId, {terms: termsWithExtensionsFilters, is_or_search: false, include_deleted_channels: viewArchivedChannels, time_zone_offset: timezoneOffset, page: 0, per_page: 20}));
        return Promise.all([filesPromise, messagesPromise]);
    };
}

export function filterFilesSearchByExt(extensions: string[]) {
    return (dispatch: DispatchFunc) => {
        dispatch({
            type: ActionTypes.SET_FILES_FILTER_BY_EXT,
            data: extensions,
        });
    };
}

export function showSearchResults() {
    return (dispatch: AppDispatch, getState: GetStateFunc) => {
        const state = getState() as GlobalState;

        const searchTerms = getSearchTerms(state);

        dispatch(updateRhsState(RHSStates.SEARCH));
        dispatch(updateSearchResultsTerms(searchTerms));

        return dispatch(performSearch(searchTerms));
    };
}

export function showRHSPlugin(pluggableId: string) {
    return {
        type: ActionTypes.UPDATE_RHS_STATE,
        state: RHSStates.PLUGIN,
        pluggableId,
    };
}

export function showChannelMembers(channelId: string) {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const state = getState() as GlobalState;

        let previousRhsState = getRhsState(state);
        if (previousRhsState === RHSStates.CHANNEL_MEMBERS) {
            previousRhsState = getPreviousRhsState(state);
        }
        dispatch({
            type: ActionTypes.UPDATE_RHS_STATE,
            channelId,
            state: RHSStates.CHANNEL_MEMBERS,
            previousRhsState,
        });

        return {data: true};
    };
}

export function hideRHSPlugin(pluggableId: string) {
    return (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const state = getState() as GlobalState;

        if (getPluggableId(state) === pluggableId) {
            dispatch(closeRightHandSide());
        }

        return {data: true};
    };
}

export function toggleRHSPlugin(pluggableId: string) {
    return (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const state = getState() as GlobalState;

        if (getPluggableId(state) === pluggableId && getRhsState(state) === RHSStates.PLUGIN) {
            dispatch(hideRHSPlugin(pluggableId));
            return {data: false};
        }

        dispatch(showRHSPlugin(pluggableId));
        return {data: true};
    };
}

export function showFlaggedPosts() {
    return {
        type: ActionTypes.UPDATE_RHS_STATE,
        state: RHSStates.FLAG,
    };
}

export function showPinnedPosts(channelId?: string) {
    return async (dispatch: DispatchFunc, getState: GetStateFunc) => {
        const state = getState() as GlobalState;
        const currentChannelId = getCurrentChannelId(state);
        const teamId = getCurrentTeamId(state);

        let previousRhsState = getRhsState(state);
        if (previousRhsState === RHSStates.PIN) {
            previousRhsState = getPreviousRhsState(state);
        }
        dispatch({
            type: ActionTypes.UPDATE_RHS_STATE,
            channelId: channelId || currentChannelId,
            state: RHSStates.PIN,
            previousRhsState,
        });

        const results = await dispatch(getPinnedPosts(channelId || currentChannelId));

        //https://time-sentry.tinkoff.ru/organizations/sentry/issues/553
        if (results.error) {
            return {data: false};
        }

        let data: any;
        if ('data' in results) {
            data = results.data;
        }

        dispatch({
            type: SearchTypes.RECEIVED_SEARCH_POSTS,
            data,
        });

        dispatch({
            type: SearchTypes.RECEIVED_SEARCH_TERM,
            data: {
                teamId,
                terms: null,
                isOrSearch: false,
            },
        });

        return {data: true};
    };
}

export function showChannelFiles(channelId: string) {
    return async (dispatch: (action: Action, getState?: GetStateFunc | null) => Promise<ActionResult|[ActionResult, ActionResult]>, getState: GetStateFunc) => {
        const state = getState() as GlobalState;
        const teamId = getCurrentTeamId(state);

        let previousRhsState = getRhsState(state);
        if (previousRhsState === RHSStates.CHANNEL_FILES) {
            previousRhsState = getPreviousRhsState(state);
        }
        dispatch({
            type: ActionTypes.UPDATE_RHS_STATE,
            channelId,
            state: RHSStates.CHANNEL_FILES,
            previousRhsState,
        });

        const results = await dispatch(performSearch('channel:' + channelId));
        const fileData = results instanceof Array ? results[0].data : null;
        const missingPostIds: string[] = [];

        if (fileData) {
            Object.values(fileData.file_infos).forEach((file: any) => {
                const postId = file?.post_id;

                if (postId && !getPost(state, postId)) {
                    missingPostIds.push(postId);
                }
            });
        }
        if (missingPostIds.length > 0) {
            await dispatch(getPostsByIds(missingPostIds));
        }

        let data: any;
        if (results && results instanceof Array && results.length === 2 && 'data' in results[1]) {
            data = results[1].data;
        }

        dispatch({
            type: SearchTypes.RECEIVED_SEARCH_POSTS,
            data,
        });
        dispatch({
            type: SearchTypes.RECEIVED_SEARCH_TERM,
            data: {
                teamId,
                terms: null,
                isOrSearch: false,
            },
        });

        return {data: true};
    };
}

export function showChannelInfo(channelId: string) {
    return (dispatch: DispatchFunc) => {
        dispatch({
            type: ActionTypes.UPDATE_RHS_STATE,
            channelId,
            state: RHSStates.CHANNEL_INFO,
        });
    };
}

/*
    Данные о userе должны быть загружены заранее, перед вызовом этого actiona,
    Для вывода нового профиля, нужно дергать допольнительную ручку бэка.
    см. пример: features/groups_mention/components/group_member_row.tsx (29)
*/
export function showUserInfo(userId: string) {
    return (dispatch: DispatchFunc) => {
        dispatch({
            type: ActionTypes.UPDATE_RHS_STATE,
            userId,
            state: RHSStates.USER_INFO,
        });
    };
}

export const cleanSelectedPost = () => ({
    type: ActionTypes.SELECT_POST,
    postId: '',
    channelId: '',
    timestamp: 0,
});

export function closeRightHandSide() {
    return (dispatch: DispatchFunc) => {
        dispatch({
            type: ActionTypes.UPDATE_RHS_STATE,
            state: null,
        });
        dispatch(cleanSelectedPost());
        return {data: true};
    };
}

export const toggleMenu = () => (dispatch: DispatchFunc) => dispatch({
    type: ActionTypes.TOGGLE_RHS_MENU,
});

export const openMenu = () => (dispatch: DispatchFunc) => dispatch({
    type: ActionTypes.OPEN_RHS_MENU,
});

export const closeMenu = () => (dispatch: DispatchFunc) => dispatch({
    type: ActionTypes.CLOSE_RHS_MENU,
});

export function setRhsExpanded(expanded: boolean) {
    return {
        type: ActionTypes.SET_RHS_EXPANDED,
        expanded,
    };
}

export function toggleRhsExpanded() {
    return {
        type: ActionTypes.TOGGLE_RHS_EXPANDED,
    };
}

export function selectPost(post: Post) {
    return {
        type: ActionTypes.SELECT_POST,
        postId: post.root_id || post.id,
        channelId: post.channel_id,
        timestamp: Date.now(),
    };
}

export function highlightReply(post: Post) {
    return {
        type: ActionTypes.HIGHLIGHT_REPLY,
        postId: post.id,
    };
}

export const clearHighlightReply = {
    type: ActionTypes.CLEAR_HIGHLIGHT_REPLY,
};

export const debouncedClearHighlightReply = debounce((dispatch) => {
    return dispatch(clearHighlightReply);
}, Constants.PERMALINK_FADEOUT);

export function selectPostAndHighlight(post: Post) {
    return (dispatch: DispatchFunc) => {
        dispatch(selectPost(post));
        dispatch(highlightReply(post));

        debouncedClearHighlightReply(dispatch);

        return {data: true};
    };
}

export function selectPostCard(post: Post) {
    return {type: ActionTypes.SELECT_POST_CARD, postId: post.id, channelId: post.channel_id};
}

export function openRHSSearch() {
    return (dispatch: DispatchFunc) => {
        dispatch(clearSearch());
        dispatch(updateSearchTerms(''));
        dispatch(updateSearchResultsTerms(''));

        dispatch(updateRhsState(RHSStates.SEARCH));

        return {data: true};
    };
}

export function openAtPrevious(previous: any) { // TODO Could not find the proper type. Seems to be in several props around
    return (dispatch: DispatchFunc, getState: GetStateFunc) => {
        if (!previous) {
            return openRHSSearch()(dispatch);
        }

        if (previous.isPinnedPosts) {
            return showPinnedPosts()(dispatch, getState);
        }
        if (previous.isFlaggedPosts) {
            return dispatch(showFlaggedPosts());
        }
        if (previous.selectedPostId) {
            const post = getPost(getState(), previous.selectedPostId);
            return post ? selectPostFromRightHandSideSearchWithPreviousState(post, previous.previousRhsState)(dispatch, getState) : openRHSSearch()(dispatch);
        }
        if (previous.selectedPostCardId) {
            const post = getPost(getState(), previous.selectedPostCardId);
            return post ? selectPostCardFromRightHandSideSearchWithPreviousState(post, previous.previousRhsState)(dispatch, getState) : openRHSSearch()(dispatch);
        }
        if (previous.searchVisible) {
            return showSearchResults()(dispatch, getState);
        }

        return openRHSSearch()(dispatch);
    };
}

export const suppressRHS = {
    type: ActionTypes.SUPPRESS_RHS,
};

export const unsuppressRHS = {
    type: ActionTypes.UNSUPPRESS_RHS,
};
